import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import {
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridRowParams,
} from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';
import { StyledDataGrid } from '../../shared-theme/StyledComponents';
import { TABLE_PAGE_SIZE_OPTIONS } from '../../utils/tableVariables';
import { PageTitle } from '../../components/StyledComponents';

export type FormListRow = { id: GridRowId } & Record<string, any>;

export type FormListRowAction<RowType> = {
  label: string;
  onClick: (row: RowType) => void;
  disabled?: (row: RowType) => boolean;
  hidden?: (row: RowType) => boolean;
};

export type WorkflowFilter = {
  value: string;
  label: string;
};

export interface FormListProps<RowType extends FormListRow> {
  // Required props
  columns: GridColDef<any>[];
  rows: RowType[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;

  // Optional data/state props
  loading?: boolean;
  error?: boolean;
  rowActions?: FormListRowAction<RowType>[];
  onRowClick?: (row: RowType) => void;
  getRowId?: (row: any) => any;
  disableRowSelectionOnClick?: boolean;
  pageSizeOptions?: number[];
  paginationMode?: 'client' | 'server';

  // Optional header props
  title?: string;
  createButtonLabel?: string;
  onCreateClick?: () => void;
  additionalHeaderButtons?: React.ReactNode;

  // Optional filter/search props
  workflowFilters?: WorkflowFilter[];
  workflowFilter?: string;
  onWorkflowFilterChange?: (value: string) => void;
  quickFilterCounts?: Record<string, number>;
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  onRefresh?: () => void;
}

/**
 * Shared DataGrid wrapper that wires up pagination, row actions, workflow filters, and consistent styling.
 */
const FormList = <RowType extends FormListRow>(
  props: FormListProps<RowType>,
) => {
  const {
    columns,
    rows,
    rowCount,
    loading = false,
    error,
    paginationModel,
    onPaginationModelChange,
    rowActions,
    onRowClick,
    getRowId,
    disableRowSelectionOnClick = true,
    pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS,
    paginationMode = 'server',
    title,
    createButtonLabel,
    onCreateClick,
    additionalHeaderButtons,
    workflowFilters,
    workflowFilter,
    onWorkflowFilterChange,
    quickFilterCounts,
    search,
    onSearchChange,
    searchPlaceholder = 'Search',
    onRefresh,
  } = props;

  const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<RowType | null>(null);

  const renderQuickFilterLabel = (filter: WorkflowFilter) => {
    if (!quickFilterCounts) return filter.label;

    const badgeCount = quickFilterCounts[filter.value] ?? 0;
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
        <span>{filter.label}</span>
        {loading ? (
          <CircularProgress size={16} sx={{ color: '#1f4D83' }} />
        ) : (
          <Box
            sx={{
              minWidth: 28,
              height: 20,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1,
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              bgcolor: '#1f4D83',
              color: '#FFFFFF',
              lineHeight: 1,
            }}
          >
            {badgeCount}
          </Box>
        )}
      </Box>
    );
  };

  const resolvedColumns = useMemo(() => {
    if (!rowActions?.length) return columns;
    const hasActionColumn = columns.some((col) => col.field === '__actions');
    if (hasActionColumn) return columns;

    const actionCol: GridColDef<any> = {
      field: '__actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const hasAvailableActions = rowActions.some(
          (action) => !action.hidden?.(params.row),
        );

        return (
          <IconButton
            size="small"
            aria-label="Row actions"
            disabled={!hasAvailableActions}
            onClick={(event) => {
              if (!hasAvailableActions) return;
              event.stopPropagation();
              setMenuRow(params.row);
              setActionsAnchor(event.currentTarget);
            }}
            sx={{
              color: 'text.primary',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'action.hover',
              },
              '&.Mui-disabled': {
                color: 'action.disabled',
              },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        );
      },
    };

    return [...columns, actionCol];
  }, [columns, rowActions]);

  const handleCloseMenu = () => {
    setActionsAnchor(null);
    setMenuRow(null);
  };

  const visibleMenuActions =
    rowActions?.filter((action) => menuRow && !action.hidden?.(menuRow)) ?? [];
  const isMenuOpen = Boolean(actionsAnchor && visibleMenuActions.length);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Optional Header */}
      {(title || onCreateClick || additionalHeaderButtons || onRefresh) && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          sx={{ mb: 1, px: 1 }}
        >
          {title && (
            <PageTitle sx={{ flexGrow: 1 }}>
              {title}
            </PageTitle>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {additionalHeaderButtons}
            {onRefresh && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={onRefresh}
                disabled={loading}
                sx={{
                  fontFamily: 'Arial',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none',
                  },
                  '&:active': {
                    boxShadow: 'none',
                  },
                  '&:focus': {
                    boxShadow: 'none',
                  },
                }}
              >
                Refresh
              </Button>
            )}
            {onCreateClick && createButtonLabel && (
              <Button
                variant="contained"
                onClick={onCreateClick}
                size="small"
                sx={{
                  textTransform: 'none',
                  boxShadow: 'none',
                  border: 'none',
                  outline: 'none',
                  '&:hover': {
                    boxShadow: 'none',
                    border: 'none',
                    outline: 'none',
                  },
                  '&:active': {
                    boxShadow: 'none',
                    border: 'none',
                    outline: 'none',
                  },
                  '&:focus': {
                    boxShadow: 'none',
                    border: 'none',
                    outline: 'none',
                  },
                }}
              >
                {createButtonLabel}
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      {/* Optional Filters and Search */}
      {(workflowFilters || onSearchChange) && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', pt: 1, px: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              pb: 1.5,
            }}
          >
            {onSearchChange && (
              <TextField
                placeholder={searchPlaceholder}
                value={search ?? ''}
                onChange={(e) => onSearchChange(e.target.value)}
                size="small"
                sx={{
                  width: { xs: '100%', md: 320 },
                  flexGrow: 1,
                  maxWidth: '25%',
                }}
              />
            )}
            {workflowFilters &&
              workflowFilter !== undefined &&
              onWorkflowFilterChange && (
                <Tabs
                  value={workflowFilter}
                  onChange={(_, value) => {
                    onWorkflowFilterChange(value);
                    onPaginationModelChange({ ...paginationModel, page: 0 });
                  }}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: 'auto',
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#1f4D83',
                    },
                    '& .MuiTab-root': {
                      color: 'text.primary',
                      outline: 'none',
                      '&:focus': {
                        outline: 'none',
                      },
                      '&:focus-visible': {
                        outline: 'none',
                      },
                    },
                    '& .Mui-selected': {
                      color: '#1f4D83',
                      '& span': {
                        color: '#1f4D83',
                      },
                    },
                  }}
                >
                  {workflowFilters.map((filter) => (
                    <Tab
                      key={filter.value}
                      value={filter.value}
                      sx={{ textTransform: 'none', minHeight: 'auto', py: 1.5 }}
                      label={renderQuickFilterLabel(filter)}
                    />
                  ))}
                </Tabs>
              )}
          </Box>
        </Box>
      )}

      {error && (
        <Box sx={{ mb: 2, px: 1 }}>
          <Alert severity="error">Unable to load data. Please try again.</Alert>
        </Box>
      )}

      <Box sx={{ flexGrow: 1, minHeight: 0, mt: 1, px: 1 }}>
        <StyledDataGrid
          rows={rows}
          columns={resolvedColumns}
          loading={loading}
          pagination
          disableRowSelectionOnClick={disableRowSelectionOnClick}
          paginationMode={paginationMode}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={pageSizeOptions}
          onRowClick={
            onRowClick
              ? (params: GridRowParams<RowType>) => onRowClick(params.row)
              : undefined
          }
          getRowId={
            getRowId ||
            ((row: any) => {
              // Fallback: try common ID field names
              const baseId =
                (row as any).id ||
                (row as any).trId ||
                (row as any).tarId ||
                (row as any).ctpId ||
                (row as any).ripId ||
                (row as any).pnrId;
              return baseId || `row-${Math.random()}`;
            })
          }
          autoHeight
          sx={(theme) => ({
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'transparent',
            minHeight: 150,
            maxHeight: `calc(${paginationModel.pageSize} * 52px + 160px)`,
            '& .MuiDataGrid-main': {
              bgcolor: 'transparent',
            },
            '& .MuiDataGrid-virtualScroller': {
              minHeight: 52,
              maxHeight: `calc(${paginationModel.pageSize} * 52px)`,
              overflowY: 'auto',
            },
            '& .MuiDataGrid-columnHeaders': {
           
              borderBottom: `1px solid ${theme.palette.divider} !important`,
            },
            '& .MuiDataGrid-columnHeader': {
            
              borderBottom: 'none',
              outline: 'none',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-within': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-row': {
              '& .MuiDataGrid-cell': {
                borderTop: 'none !important',
                borderBottom: `1px solid ${theme.palette.divider} !important`,
              },
            },
            '& .MuiDataGrid-cell': {
              borderTop: 'none !important',
              borderBottom: `1px solid ${theme.palette.divider} !important`,
            },
            '& .MuiDataGrid-footerContainer': {
           
              borderTop: 'none !important',
            },
          })}
          slotProps={{
            pagination: {
              showFirstButton: true,
              showLastButton: true,
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={actionsAnchor}
        open={isMenuOpen}
        onClose={handleCloseMenu}
      >
        {visibleMenuActions.map((action) => (
          <MenuItem
            key={action.label}
            onClick={() => {
              if (!menuRow) return;
              action.onClick(menuRow);
              handleCloseMenu();
            }}
            disabled={menuRow ? action.disabled?.(menuRow) : false}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default FormList;
